/**
 * GuidedCreationPlugin - Step-by-step guided activity type.
 *
 * New plugin for the Activity Engine. Guides children through
 * a sequence of steps (drag specific blocks), then opens free play.
 * Designed for pre-readers with icon-only prompts.
 *
 * @see GitHub Issue #852
 */

import StepIndicator from './StepIndicator';
import PaletteRestrictor from './PaletteRestrictor';

export default class GuidedCreationPlugin {
    constructor () {
        this._activityData = null;
        this._stateMachine = null;
        this._container = null;
        this._stepIndicator = null;
        this._paletteRestrictor = null;
        this._steps = [];
        this._currentStepIndex = 0;
        this._completedSteps = [];
        this._freePlayBlocks = 0;
        this._bonusesEarned = [];
        this._blockCheckInterval = null;
    }

    /**
     * Initialize the plugin for a specific activity.
     * @param {object} activityData - Validated activity definition
     * @param {object} stateMachine - ActivityStateMachine instance
     * @param {HTMLElement} container - Editor container
     */
    init (activityData, stateMachine, container) {
        this._activityData = activityData;
        this._stateMachine = stateMachine;
        this._container = container;
        this._steps = activityData.scaffolding.steps || [];
        this._completedSteps = [];
        this._freePlayBlocks = 0;
        this._bonusesEarned = [];

        // Set up palette restrictor
        if (activityData.scaffolding.availableBlocks) {
            this._paletteRestrictor = new PaletteRestrictor(
                container,
                activityData.scaffolding.availableBlocks
            );
        }

        // Set up step indicator
        this._stepIndicator = new StepIndicator(container, this._steps);

        // Start block monitoring for step completion
        this._startBlockMonitoring();
    }

    /**
     * React to state machine transitions.
     */
    onStateChange (prevState, newState, meta) {
        switch (newState) {
        case 'GUIDED_STEP':
            this._currentStepIndex = meta.stepIndex || 0;
            this._showCurrentStep();
            break;

        case 'FREE_PLAY':
            this._enterFreePlay();
            break;

        case 'EVALUATING':
            this._evaluateBonuses();
            break;

        case 'READY':
            this._resetUI();
            break;
        }
    }

    /**
     * Evaluate current state against success criteria.
     * @returns {object}
     */
    evaluate () {
        return {
            stepsCompleted: this._completedSteps.length,
            totalSteps: this._steps.length,
            bonusesEarned: this._bonusesEarned,
            freePlayBlocks: this._freePlayBlocks,
            success: this._completedSteps.length >= (this._activityData.successCriteria.requiredSteps || this._steps.length)
        };
    }

    /**
     * Get current block count in scripting area.
     * @returns {number}
     */
    getBlockCount () {
        return this._countBlocks();
    }

    /**
     * Show a hint (from HintManager).
     * @param {object} hint
     */
    showHint (hint) {
        if (hint.tier === 0 && this._stepIndicator) {
            // Tier 0: text nudge — pulse the current step indicator
            this._stepIndicator.pulseCurrentStep();
        } else if (hint.tier === 1 && hint.svg) {
            // Tier 1: SVG overlay showing the target action
            this._showSVGOverlay(hint.svg);
        } else if (hint.tier === 2) {
            // Tier 2: Explicit block-by-block guidance
            this._stepIndicator.showExplicitGuide(this._currentStepIndex);
        }
    }

    /**
     * Show full walkthrough (give-up scenario).
     */
    showWalkthrough () {
        if (this._stepIndicator) {
            this._stepIndicator.showAllSteps();
        }
    }

    destroy () {
        if (this._blockCheckInterval) {
            clearInterval(this._blockCheckInterval);
        }
        if (this._stepIndicator) {
            this._stepIndicator.destroy();
        }
        if (this._paletteRestrictor) {
            this._paletteRestrictor.destroy();
        }
        this._activityData = null;
        this._stateMachine = null;
    }

    // --- Private methods ---

    _showCurrentStep () {
        if (!this._stepIndicator) return;
        const step = this._steps[this._currentStepIndex];
        if (!step) return;

        this._stepIndicator.showStep(this._currentStepIndex);

        // Restrict palette to blocks available for this step (if specified)
        if (this._paletteRestrictor) {
            this._paletteRestrictor.apply();
        }
    }

    _enterFreePlay () {
        // Remove palette restrictions — all blocks available
        if (this._paletteRestrictor) {
            this._paletteRestrictor.remove();
        }

        // Hide step indicator, show free play prompt
        if (this._stepIndicator) {
            this._stepIndicator.showFreePlayPrompt();
        }

        // Track blocks added during free play
        this._freePlayBlocks = 0;
        const baseCount = this._countBlocks();
        if (this._blockCheckInterval) clearInterval(this._blockCheckInterval);
        this._blockCheckInterval = setInterval(() => {
            const current = this._countBlocks();
            this._freePlayBlocks = Math.max(0, current - baseCount);
        }, 500);
    }

    _evaluateBonuses () {
        const criteria = this._activityData.successCriteria;
        if (!criteria.bonusCriteria) return;

        for (const bonus of criteria.bonusCriteria) {
            if (this._checkBonusCriteria(bonus)) {
                this._bonusesEarned.push(bonus.name);
            }
        }
    }

    _checkBonusCriteria (bonus) {
        const check = bonus.check;
        if (!check) return false;

        if (check.startsWith('blockCount')) {
            // e.g., "blockCount > 6"
            const match = check.match(/blockCount\s*([><=]+)\s*(\d+)/);
            if (match) {
                const op = match[1];
                const val = parseInt(match[2], 10);
                const count = this._countBlocks();
                switch (op) {
                case '>': return count > val;
                case '>=': return count >= val;
                case '<': return count < val;
                case '==': return count === val;
                }
            }
        }

        if (check.startsWith('hasCategory:')) {
            const category = check.replace('hasCategory:', '');
            return this._hasBlockCategory(category);
        }

        return false;
    }

    _startBlockMonitoring () {
        // Poll for block additions to detect step completion
        this._blockCheckInterval = setInterval(() => {
            if (this._stateMachine.state !== 'GUIDED_STEP') return;
            const step = this._steps[this._currentStepIndex];
            if (!step) return;

            if (this._isStepComplete(step)) {
                this._completeCurrentStep();
            }
        }, 300);
    }

    _isStepComplete (step) {
        if (!step.successCheck) return false;

        // Parse successCheck string
        if (step.successCheck.startsWith('hasBlock:')) {
            const blockType = step.successCheck.replace('hasBlock:', '');
            return this._hasBlock(blockType);
        }

        return false;
    }

    _completeCurrentStep () {
        if (this._completedSteps.includes(this._currentStepIndex)) return;
        this._completedSteps.push(this._currentStepIndex);

        // Play step celebration
        const step = this._steps[this._currentStepIndex];
        if (this._stepIndicator) {
            this._stepIndicator.celebrateStep(this._currentStepIndex, step.celebration || 'sparkle');
        }

        // Advance to next step or free play
        const nextIndex = this._currentStepIndex + 1;
        if (nextIndex < this._steps.length) {
            // Small delay for celebration before advancing
            setTimeout(() => {
                this._stateMachine.transition('GUIDED_STEP', {stepIndex: nextIndex});
            }, 800);
        } else {
            // All steps complete — enter free play
            setTimeout(() => {
                this._stateMachine.transition('FREE_PLAY');
            }, 1200);
        }
    }

    _resetUI () {
        this._completedSteps = [];
        this._freePlayBlocks = 0;
        this._bonusesEarned = [];
        this._currentStepIndex = 0;
        if (this._stepIndicator) {
            this._stepIndicator.reset();
        }
    }

    // --- ScratchJr integration helpers ---

    /**
     * Count blocks in the current project's scripting area.
     * Uses ScratchJr's internal DOM structure.
     */
    _countBlocks () {
        // ScratchJr renders blocks as SVG elements in the scripts container
        const scripts = this._container.querySelectorAll('.scripts .blockgroup');
        return scripts ? scripts.length : 0;
    }

    /**
     * Check if a specific block type exists in the scripting area.
     * @param {string} blockType - e.g., 'forward', 'hop', 'repeat'
     */
    _hasBlock (blockType) {
        // ScratchJr blocks have data attributes or class names indicating type
        const blocks = this._container.querySelectorAll('.scripts [data-block-type]');
        for (const block of blocks) {
            if (block.getAttribute('data-block-type') === blockType) {
                return true;
            }
        }
        // Fallback: check by block class naming convention
        const byClass = this._container.querySelectorAll(`.scripts .block-${blockType}`);
        return byClass && byClass.length > 0;
    }

    /**
     * Check if any block from a category exists.
     * @param {string} category - e.g., 'looks', 'sound'
     */
    _hasBlockCategory (category) {
        const blocks = this._container.querySelectorAll('.scripts [data-block-category]');
        for (const block of blocks) {
            if (block.getAttribute('data-block-category') === category) {
                return true;
            }
        }
        return false;
    }

    _showSVGOverlay (svgPath) {
        const overlay = document.createElement('div');
        overlay.className = 'activity-hint-svg-overlay';
        const img = document.createElement('img');
        img.src = svgPath;
        img.className = 'activity-hint-svg';
        overlay.appendChild(img);
        overlay.addEventListener('pointerup', () => {
            if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        });
        this._container.appendChild(overlay);
    }
}
